import { RefObject, useCallback, useRef, useState } from "react";
import { useAboveFold } from "web/react/hooks/use-above-fold/use-above-fold";
import useIsomorphicLayoutEffect from "web/react/hooks/use-isomorphic-layout-effect/use-isomorphic-layout-effect";
import analytics from "web/script/analytics/analytics";

interface UseEditorialAnalyticsProps {
    label: string;
    editorialLabel: string;
    position: number;
    isSponsored: boolean;
    productPosition?: number;
    productId?: number;
    buyOptionId?: number | null;
    franchise: string;
    tags?: string[];
    gender: "womenswear" | "menswear" | "mixed";
    visibleThreshold?: number;
}

interface UseEditorialAnalyticsResults {
    containerRef: RefObject<HTMLDivElement>;
    sendClickEvent: (action: string) => void;
}

export function useEditorialAnalytics(
    params: UseEditorialAnalyticsProps
): UseEditorialAnalyticsResults {
    const {
        label,
        editorialLabel,
        position,
        isSponsored,
        productPosition,
        productId,
        buyOptionId,
        franchise,
        gender,
        tags,
        visibleThreshold,
    } = params;

    const containerRef = useRef<HTMLDivElement>(null);
    const { isAboveFold, isVisible } = useAboveFold(containerRef, visibleThreshold || 0.5);
    const [hasImpression, setHasImpression] = useState(false);

    const sendAnalyticsEvent = useCallback(
        (action: string, area?: string) => {
            analytics.event(
                "editorial",
                action,
                label,
                action !== "clicked", // nonInteraction
                {}, // customData
                "", // subType
                {}, // checkoutData
                [
                    // itemData
                    {
                        item_type: "editorial",
                        above_fold: isAboveFold,
                        ...(editorialLabel && { editorial_label: editorialLabel }),
                        component_type: label,
                        position: position,
                        sponsored: isSponsored,
                        area: area,
                        franchise: franchise,
                        editorial_gender: gender,
                        tags: tags,
                        ...(typeof productPosition === "number" && {
                            product_position: productPosition,
                        }),
                        ...(productId && { product_id: productId }),
                        ...(buyOptionId && { buy_option_id: buyOptionId }),
                    },
                ]
            );
        },
        [
            label,
            editorialLabel,
            isAboveFold,
            position,
            isSponsored,
            productPosition,
            productId,
            buyOptionId,
            franchise,
            tags,
            gender,
        ]
    );

    function sendClickEvent(area: string): void {
        sendAnalyticsEvent("clicked", area);
    }

    useIsomorphicLayoutEffect(() => {
        if (isVisible && !hasImpression) {
            sendAnalyticsEvent("viewed");
            setHasImpression(true);
        }
    }, [isVisible, hasImpression, sendAnalyticsEvent, setHasImpression]);

    return { containerRef, sendClickEvent };
}
